import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import { useParams } from 'react-router-dom';
import { unstable_batchedUpdates } from 'react-dom';
import { Flex, Box } from 'rebass';
import pick from 'lodash/pick';
import { useWindowSize } from 'react-use';
import useSWR from 'swr';
import useEntity from 'entities/hooks/use-entity';
import useHistory from 'app/hooks/history';
import Modal from 'app/modal/v2';
import ImageEditor from './ImageEditor';
import { get, getSWR, patch } from 'utils/api';
import Grid from 'app/grid/index';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import Loading from 'story-pages/list/loading';
import { useIsSideBarClosed } from 'ui/hooks';
import SliderPageRenderer from './SliderComponents/SliderPageRenderer';
import { getCategory, transformAllPages, transformSinglePage } from '../helper';
import FlipbookPanel from './FlipbookPanel';
import useLocation from 'app/hooks/location';
import TextForm from 'story-media/text-form/form';
import { updateTextMedium } from 'story-media/api';
import { Formik } from 'formik';
import { calculateMaxCharacters } from 'story-media/text-form/calculateMaxCharacters';
import { CurrentCollectionContext } from 'collections/current-collection-context';
import usePageLock from '../hooks/usePageLock';
import { useDispatch } from 'react-redux';

// ─── Module-scope zoom calculator ────────────────────────────────────────────
function calculateZoomValue(width, baseZoom = 0.15, isFromModal = false) {
  const baseWidth = 1512;
  const screenWidth = isFromModal && width > 1600 ? (width / 100) * 90 : width;
  const widthDivider =
    isFromModal && width > 1600
      ? baseWidth / screenWidth
      : screenWidth / baseWidth;
  return baseZoom * widthDivider * 100;
}

// ─── useFlipAnimation ────────────────────────────────────────────────────────
// Manages all flip animation state and drag/swipe interaction.
// Forward flip: right page rotates -180° around left edge (spine).
// Backward flip: left page rotates +180° around right edge (spine).
function useFlipAnimation({ spreadIndex, totalSpreads, onSpreadChange }) {
  // 'animating-forward' | 'animating-backward' | null
  const [flipPhase, setFlipPhase] = useState(null);
  // Degrees: 0 = rest, -180 = forward end, +180 = backward end
  const [flipRotation, setFlipRotation] = useState(0);
  // Progressive drag rotation (null = not dragging)
  const [dragRotation, setDragRotation] = useState(null);

  // Drag state in a ref to avoid stale-closure issues in pointer-event handlers
  const dragRef = useRef({ active: false, direction: null, startX: 0, currentRotation: 0 });

  const animTimerRef = useRef(null);
  const rAFRef = useRef(null);

  const isAnimating = flipPhase !== null;
  const isDragging = dragRotation !== null;
  const canFlipForward = spreadIndex < totalSpreads - 1;
  const canFlipBackward = spreadIndex > 0;

  const showFlippingElement = isAnimating || isDragging;
  const flipDirection =
    isDragging ? dragRef.current.direction
      : flipPhase === 'animating-forward' ? 'forward'
        : flipPhase === 'animating-backward' ? 'backward'
          : null;
  const activeRotation = isDragging ? dragRotation : flipRotation;

  const triggerFlip = useCallback((direction) => {
    if (isAnimating) return;
    if (direction === 'forward' && !canFlipForward) return;
    if (direction === 'backward' && !canFlipBackward) return;

    // Clear any lingering drag state before starting CSS-driven animation
    setDragRotation(null);
    setFlipRotation(0);
    setFlipPhase(`animating-${direction}`);

    // Double rAF: ensures the initial rotation (0) is painted before the
    // transition kicks in, preventing a snap-to-end on the first frame.
    if (rAFRef.current) cancelAnimationFrame(rAFRef.current);
    rAFRef.current = requestAnimationFrame(() => {
      rAFRef.current = requestAnimationFrame(() => {
        setFlipRotation(direction === 'forward' ? -180 : 180);
        animTimerRef.current = setTimeout(() => {
          unstable_batchedUpdates(() => {
            onSpreadChange(direction === 'forward' ? spreadIndex + 1 : spreadIndex - 1);
            setFlipPhase(null);
            setFlipRotation(0);
          });
        }, 1200); // slightly > 600ms CSS transition to avoid visual flash
      });
    });
  }, [isAnimating, canFlipForward, canFlipBackward, spreadIndex, onSpreadChange]);

  const flipForward = useCallback(() => triggerFlip('forward'), [triggerFlip]);
  const flipBackward = useCallback(() => triggerFlip('backward'), [triggerFlip]);

  const onDragStart = useCallback((clientX, side) => {
    if (isAnimating) return;
    if (side === 'right' && !canFlipForward) return;
    if (side === 'left' && !canFlipBackward) return;
    dragRef.current = {
      active: true,
      direction: side === 'right' ? 'forward' : 'backward',
      startX: clientX,
      currentRotation: 0,
    };
    setDragRotation(0);
  }, [isAnimating, canFlipForward, canFlipBackward]);

  const onDragMove = useCallback((clientX, pageWidth) => {
    if (!dragRef.current.active) return;
    const delta = clientX - dragRef.current.startX;
    const pw = pageWidth || 300;
    const rotation =
      dragRef.current.direction === 'forward'
        ? Math.max(-180, Math.min(0, (delta / pw) * -180))
        : Math.max(0, Math.min(180, (delta / pw) * 180));
    dragRef.current.currentRotation = rotation;
    setDragRotation(rotation);
  }, []);

  const onDragEnd = useCallback(() => {
    if (!dragRef.current.active) return;
    const { direction, currentRotation } = dragRef.current;
    dragRef.current = { active: false, direction: null, startX: 0, currentRotation: 0 };
    setDragRotation(null);
    // Complete the flip if dragged past 45° threshold; otherwise spring back
    if (Math.abs(currentRotation) > 45) {
      triggerFlip(direction);
    }
  }, [triggerFlip]);

  useEffect(() => () => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    if (rAFRef.current) cancelAnimationFrame(rAFRef.current);
  }, []);

  return {
    flipPhase,
    flipRotation: activeRotation,
    isAnimating,
    isDragging,
    showFlippingElement,
    flipDirection,
    canFlipForward,
    canFlipBackward,
    flipForward,
    flipBackward,
    onDragStart,
    onDragMove,
    onDragEnd,
  };
}

// ─── PageFace ─────────────────────────────────────────────────────────────────
// Renders one page inside the flipbook. Shows a lined blank for missing pages.
const PageFace = React.memo(function PageFace({
  page,
  index,
  zoomValue,
  sliderHeight,
  selectedPageIndex,
  setCurrentPage,
  onMediaClick,
  selectedLayer,
  setSelectedLayer,
  heightRef,
  widthRef,
  pointerEvents = 'auto',
}) {
  if (!page) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          bg: '#f5efe0',
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(0,0,0,0.05) 28px)',
        }}
      />
    );
  }

  return (
    // <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', bg: '#fff' }}>
    <Box
      sx={{
        transform: `scale(${zoomValue / 100})`,
        transformOrigin: 'top left',
        width: 'max-content',
        height: sliderHeight + 'px',
        pointerEvents,
      }}
      ref={widthRef}
    >
      <Flex ref={heightRef}>
        <SliderPageRenderer
          isFromSlider
          index={index}
          zoom={100}
          {...page}
          attributes={page.attributes || page}
          isSelected={selectedPageIndex === index}
          setCurrentPage={setCurrentPage}
          onMediaClick={onMediaClick}
          selectedLayer={selectedLayer}
          setSelectedLayer={setSelectedLayer}
        />
      </Flex>
    </Box>
  );
});

// ─── PageSlider ───────────────────────────────────────────────────────────────
export default function PageSlider({ boxWidth, firstBookId,
  currentPage,
  setCurrentPage,
  selectedPageIndex,
  setSelectedPageIndex,
  totalPagesData,
  setTotalPagesData,
  loading,
  isReadOnly = false,
  HeaderComponent,
  // Move/resize media editing — undefined in read-only contexts (e.g. the
  // Talking Family Tree preview), so every usage below is optional-chained.
  layerEditor,
}) {
  const params = useParams();
  const { entity: paramEntity } = useEntity(params);
  const currentJournalId = paramEntity?.id

  const [sliderHeight, setSliderHeight] = useState(0);
  const [innerBoxWidth, setInnerBoxWidth] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [selectedLayerPageIndex, setSelectedLayerPageIndex] = useState(null);
  const [changeTemplate, setChangeTemplate] = useState(null);
  const [updatingTemplate, setUpdatingTemplate] = useState(false);
  const [containerHeight, setContainerHeight] = useState(null);
  const [selectedImageLayer, setSelectedImageLayer] = useState(null);
  const [selectedImageLayerData, setSelectedImageLayerData] = useState({});

  const { revalidate: revalidatePage } = useSWR(
    (currentPage && !isReadOnly) ? `/v3/books/${firstBookId}/pages/${currentPage?.id}` : null
  );

  const { pathname } = useLocation();

  const { receiveDeletion, update } = useEntityActionCreators();
  const { entity } = useEntity(selectedImageLayer?.['source-media'] || {});

  const { push } = useHistory();
  // ─── Spread-level navigation ──────────────────────────────────────────────
  const spreadIndex = Math.floor(selectedPageIndex / 2);
  const totalSpreads = Math.ceil(totalPagesData.length / 2);
  // Page possibilities are fetched individually for each page in the current
  // spread so that "Change Layout" under each page shows that page's options.
  const currentLeftPage = totalPagesData[spreadIndex * 2]
  const leftPageId = currentLeftPage?.id;
  const currentRightPage = totalPagesData[spreadIndex * 2 + 1]
  const rightPageId = currentRightPage?.id;

  const { data, revalidate: revalidateBookStories } = useSWR(
    (getCategory(currentLeftPage?.kind) === null && leftPageId && !isReadOnly) ? `v3/books/${firstBookId}/pages/${leftPageId}/story` : null
  );
  const storyDetailsLeft = data?.data?.data?.attributes;

  const { data: storyData, revalidate: revalidateBookStoriesRight } = useSWR(
    (getCategory(currentRightPage?.kind) === null && rightPageId && !isReadOnly) ? `v3/books/${firstBookId}/pages/${rightPageId}/story` : null
  );

  const storyDetailsRight = storyData?.data?.data?.attributes;

  // getCategory(page?.kind) === null 
  const {
    data: leftRespData,
    revalidate: revalidateLeftPagePossibilities,
    isValidating: leftPageIsValidating,
  } = useSWR(
    (getCategory(currentLeftPage?.kind) === null && leftPageId && !isReadOnly) ? `/v3/books/${firstBookId}/pages/${leftPageId}/page_possibilities` : null,
    getSWR,
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false }
  );

  const {
    data: rightRespData,
    revalidate: revalidateRightPagePossibilities,
    isValidating: rightPageIsValidating,
  } = useSWR(
    (getCategory(currentRightPage?.kind) === null && rightPageId && !isReadOnly) ? `/v3/books/${firstBookId}/pages/${rightPageId}/page_possibilities` : null,
    getSWR,
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false }
  );

  const leftPagePossibilities = useMemo(
    () => leftRespData?.data ? transformAllPages(leftRespData?.data) : [],
    [leftRespData?.data]
  );

  const rightPagePossibilities = useMemo(
    () => rightRespData?.data ? transformAllPages(rightRespData?.data) : [],
    [rightRespData?.data]
  );
  const { width, height } = useWindowSize();
  const isSideBarClosed = useIsSideBarClosed();

  const measureRefs = useRef([]);

  const revalidateBookStoriesRef = useRef(revalidateBookStories);
  const revalidateBookStoriesRightRef = useRef(revalidateBookStoriesRight);
  useEffect(() => {
    revalidateBookStoriesRef.current = revalidateBookStories;
  }, [revalidateBookStories]);

  useEffect(() => {
    revalidateBookStoriesRightRef.current = revalidateBookStoriesRight;
  }, [revalidateBookStoriesRight]);

  // ─── Zoom ─────────────────────────────────────────────────────────────────
  // Calculates the maximum uniform scale that fits the open book (two pages
  // side-by-side) into the available panel area while preserving aspect ratio.
  //
  // Width axis:  panelWidth  / (singlePageWidth × 2)
  // Height axis: availableViewportHeight / singlePageHeight
  //              — viewport height minus space reserved for the per-page
  //                buttons (non-readonly) or the top nav bar (readonly).
  //
  // Both axes use directly measured/computed values so the zoom fills the
  // real available space on every screen size and sidebar state.
  const zoomValue = useMemo(() => {
    if (!innerBoxWidth || innerBoxWidth <= 0 || !sliderHeight) return 10;

    const sidebarOffset = isSideBarClosed ? 85 : 285;
    const availableWidth = isReadOnly
      ? width
      : Math.max(200, (width - 100) - ((boxWidth / 2 || 0) + sidebarOffset));

    // Fit both pages side-by-side within the panel width
    const widthZoom = (availableWidth / (innerBoxWidth * 2)) * 100;

    // Fit the page height within the viewport, reserving space for UI chrome:
    //   non-readonly → ~180 px for BookEditorTopBar + its gap above the book,
    //                  plus the per-page buttons row + margins below it
    //   readonly     → ~200 px for the top nav bar offset + padding
    const reservedVertical = isReadOnly ? 200 : 180;
    const heightZoom = ((height - reservedVertical) / sliderHeight) * 100;

    return Math.max(1, Math.min(widthZoom, heightZoom));
  }, [width, height, innerBoxWidth, sliderHeight, isSideBarClosed, boxWidth, isReadOnly]);

  const zoomValueModal = useMemo(
    () => calculateZoomValue(width, width > 2000 ? 0.12 : 0.11, true),
    [width]
  );
  const isFromTellAStory = pathname?.includes('tell_a_story')

  const divWidth = useMemo(() => {
    const sidebarOffset = isSideBarClosed ? 85 : 285;
    return isReadOnly ? (isFromTellAStory ? '100vw' : ['100vw', '100vw', '100vw', `calc(100vw - ${sidebarOffset - 15}px)`]) : `calc(100vw - ${(boxWidth / 2 || 0) + sidebarOffset}px)`;
  }, [isSideBarClosed, boxWidth, isReadOnly]);

  const handleSpreadChange = useCallback((newSpreadIndex) => {
    const newPageIndex = newSpreadIndex * 2;
    if (newPageIndex < totalPagesData.length) {
      setSelectedPageIndex(newPageIndex);
      setCurrentPage(totalPagesData[newPageIndex]);
    }
  }, [totalPagesData, setSelectedPageIndex, setCurrentPage]);

  const {
    flipRotation,
    isAnimating,
    showFlippingElement,
    flipDirection,
    canFlipForward,
    canFlipBackward,
    flipForward,
    flipBackward,
    onDragStart,
    onDragMove,
    onDragEnd,
  } = useFlipAnimation({ spreadIndex, totalSpreads, onSpreadChange: handleSpreadChange });
  const [showTextModal, setShowTextModal] = useState(null);
  const [checkMarkSuccess, setCheckMarkSuccess] = useState(null);

  // Recompute only when the box dimensions or font properties change, not on
  // every keystroke. showTextModal is the layer object; font props live either
  // directly on it or inside its attributes.
  const maxChars = useMemo(() => {
    if (!showTextModal) return 0;

    return calculateMaxCharacters({
      width: showTextModal.width,
      height: showTextModal.height,
      fontSize: showTextModal?.['fontSize'] ?? showTextModal.fontSize ?? 16,
      fontFamily: showTextModal?.['fontFamily'] ?? showTextModal.fontFamily ?? 'sans-serif',
      fontWeight: showTextModal?.['fontWeight'] ?? showTextModal.fontWeight ?? 'normal',
    });
  }, [
    showTextModal?.width,
    showTextModal?.height,
    showTextModal?.fontSize,
    showTextModal?.fontFamily,
    showTextModal?.fontWeight,
  ]);
  const [dataLayerAttributes, setDataLayerAttributes] = useState(null)
  const { data: dataText } = useSWR(selectedLayer?.['source-media']?.type === 'texts' ? `/v2/media/${selectedLayer?.['source-media']?.id}` : null, getSWR);

  useEffect(() => {
    setDataLayerAttributes(dataText?.data?.data?.attributes)
  }, [dataText])

  const handleNext = useCallback(() => {
    flipForward();
  }, [flipForward]);

  const handlePrev = useCallback(() => {
    flipBackward();
  }, [flipBackward]);

  // ─── Mobile swipe navigation ──────────────────────────────────────────────
  const swipeTouchStartX = useRef(null);

  const handleTouchStart = useCallback((e) => {
    swipeTouchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (swipeTouchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - swipeTouchStartX.current;
    swipeTouchStartX.current = null;
    const SWIPE_THRESHOLD = 50;
    if (delta > SWIPE_THRESHOLD) flipBackward();      // swipe right → previous
    else if (delta < -SWIPE_THRESHOLD) flipForward(); // swipe left  → next
  }, [flipBackward, flipForward]);

  // ─── Keyboard navigation ──────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (!showTextModal) {
        if (e.key === 'ArrowRight') flipForward();
        if (e.key === 'ArrowLeft') flipBackward();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [flipForward, flipBackward, showTextModal]);

  // ─── Page indices for the book ────────────────────────────────────────────
  const leftIdx = spreadIndex * 2;
  const rightIdx = spreadIndex * 2 + 1;
  const nextLeftIdx = (spreadIndex + 1) * 2;
  const nextRightIdx = (spreadIndex + 1) * 2 + 1;
  const prevLeftIdx = (spreadIndex - 1) * 2;
  const prevRightIdx = (spreadIndex - 1) * 2 + 1;

  // Static slots swap content so the flipping element reveals the correct page
  const displayLeftPage =
    showFlippingElement && flipDirection === 'backward'
      ? totalPagesData[prevLeftIdx]
      : totalPagesData[leftIdx];

  const displayRightPage =
    showFlippingElement && flipDirection === 'forward'
      ? totalPagesData[nextRightIdx]
      : totalPagesData[rightIdx];

  // ─── Page locking (independent per page — see the design's two lock badges,
  // one above each side of the spread) ───────────────────────────────────────
  const leftPageLockState = usePageLock({ firstBookId, currentPage: displayLeftPage });
  const rightPageLockState = usePageLock({ firstBookId, currentPage: displayRightPage });

  const editablePageId = currentPage?.id || (currentPage?.attributes || currentPage)?.id;
  const isEditablePageLocked =
    editablePageId && editablePageId === displayLeftPage?.id
      ? leftPageLockState.isLocked
      : editablePageId && editablePageId === displayRightPage?.id
        ? rightPageLockState.isLocked
        : false;

  // Bridges usePageLayerEditor's own API to what MediaSelectionOverlay
  // expects, adding the zoom/page-dimension context it needs to convert
  // on-screen pointer deltas into the native xpos/ypos/width/height space
  // (see layer-geometry.js) — kept here rather than in the hook itself since
  // zoomValue/page dimensions are PageSlider's concern, not the editor's.
  const nativePageWidth = (currentPage?.attributes || currentPage)?.width || 0;
  const nativePageHeight = (currentPage?.attributes || currentPage)?.height || 0;

  const overlayLayerEditor = useMemo(() => {
    if (!layerEditor) return null;
    return {
      groups: layerEditor.groups,
      layers: layerEditor.layers,
      getRect: layerEditor.getRect,
      getOverrideIfDirty: layerEditor.getOverrideIfDirty,
      selectedLayerIds: layerEditor?.selectedLayerIds,
      clearSelection: layerEditor.clearSelection,
      isLocked: isEditablePageLocked,
      zoomValue,
      pageWidth: nativePageWidth,
      pageHeight: nativePageHeight,
      onSelectLayer: layerEditor.selectLayer,
      onBeginInteraction: layerEditor.beginInteraction,
      onApplyLive: layerEditor.applyLive,
      onEndInteraction: layerEditor.endInteraction,
      onCancelInteraction: layerEditor.cancelInteraction,
    };
  }, [layerEditor, isEditablePageLocked, zoomValue, nativePageWidth, nativePageHeight]);

  const selectedLayers = (layerEditor?.layers || []).filter(layer =>
    layerEditor.selectedLayerIds.includes(layer.id)
  );
  // Crop Photo only shows when the current selection contains a layer that
  // satisfies the design's exact condition — never for system-generated images.
  const canCropSelection =
    selectedLayers.some(
      layer =>
        layer.type === 'Books::PageImageLayer' &&
        layer.kind === 'user'
    ) &&
    !selectedLayers.some(
      layer => layer["component-value"] === 'qr_code'
    )

  // Flipping element faces
  const flipFrontPage =
    flipDirection === 'forward' ? totalPagesData[rightIdx] : totalPagesData[leftIdx];
  const flipBackPage =
    flipDirection === 'forward' ? totalPagesData[nextLeftIdx] : totalPagesData[prevRightIdx];
  const flipFrontIdx = flipDirection === 'forward' ? rightIdx : leftIdx;
  const flipBackIdx = flipDirection === 'forward' ? nextLeftIdx : prevRightIdx;

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setSelectedImageLayerData(entity);
  }, [entity]);

  useEffect(() => {
    if ((currentPage?.attributes || currentPage)?.kind === 'standard') {
      revalidateLeftPagePossibilities();
      revalidateRightPagePossibilities();
      receiveDeletion({ entityType: 'book-page-possibilities' });
      receiveDeletion({ entityType: 'page-possibility-layers' });
    }
    // eslint-disable-next-line
  }, [leftPageId, rightPageId]);

  useEffect(() => {
    if (!currentPage) return;
    setSelectedLayer(null);
    window.dispatchEvent(new Event('resize'));
    revalidateBookStoriesRef.current?.();
    revalidateBookStoriesRightRef.current?.();
  }, [currentPage?.id, isSideBarClosed]);

  useLayoutEffect(() => {
    if (!measureRefs.current.length) return;

    // Use offsetWidth (CSS layout width) instead of getBoundingClientRect().width.
    // getBoundingClientRect reflects the visual size AFTER CSS transforms, so it
    // changes whenever zoomValue changes — putting zoomValue in the deps then
    // creates an infinite loop (zoom → visual width → innerBoxWidth → zoom → …).
    // offsetWidth is the intrinsic CSS box width, unaffected by transform: scale(),
    // so it stays constant across zoom changes and breaks the cycle.
    const widths = measureRefs.current.map((el) => el?.offsetWidth || 0);
    setInnerBoxWidth(Math.max(...widths));
  }, [loading, measureRefs.current.length]);

  // ─── Stable ref for page width measurement ───────────────────────────────
  const setMeasureRef = useCallback((index, el) => {
    measureRefs.current[index] = el;
  }, []);

  // ─── Stable ref for page height measurement ───────────────────────────────
  const handleSliderHeightRef = useCallback((el) => {
    if (!el) return;
    // offsetHeight gives the CSS layout height (unaffected by parent transforms).
    // getBoundingClientRect().height would reflect the current CSS scale and seed
    // sliderHeight with a wrong value before the ResizeObserver corrects it.
    const h = el.offsetHeight;
    if (h > 0) setSliderHeight(h);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const nh = entry.contentRect.height;
        if (nh > 0) setSliderHeight(nh);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ─── Navigate to spread containing a page index (used by onPageSelection) ─
  const scrollToIndex = useCallback((index) => {
    const targetSpread = Math.floor(index / 2);
    if (targetSpread !== spreadIndex) {
      handleSpreadChange(targetSpread);
    }
  }, [spreadIndex, handleSpreadChange]);

  // ─── Template change ──────────────────────────────────────────────────────
  const onPageSelection = useCallback(async (page) => {
    try {
      setUpdatingTemplate(true);
      const { status } = await patch(
        `/v3/books/${firstBookId}/pages/${currentPage?.id}?page_possibility_id=${page?.attributes?.id}`
      );
      if (status === 200 || status === 201) {
        const { data: getData } = await get(`/v3/books/${firstBookId}/pages/${currentPage?.id}`);
        const updatedTransformedLayer = getData?.initial ? transformSinglePage(getData?.initial) : [];
        revalidatePage();
        setCurrentPage(updatedTransformedLayer);
        setTotalPagesData((prev) =>
          prev.map((mainPage) =>
            mainPage?.id === currentPage?.id ? updatedTransformedLayer : mainPage
          )
        );
        const selectedIndex = totalPagesData.findIndex((p) => p.id === currentPage?.id);
        scrollToIndex(selectedIndex);
        setChangeTemplate(false);
      }
      setUpdatingTemplate(false);
    } catch (err) {
      setUpdatingTemplate(false);
    }
  }, [firstBookId, currentPage?.id, revalidatePage, setCurrentPage, setTotalPagesData, scrollToIndex, totalPagesData]);

  // pageIdx is leftIdx or rightIdx, injected by FlipbookPanel's wrapper
  // A ref keeps the current selectedLayer readable in the callback without
  // adding it to the deps array (which would invalidate the callback on every
  // selection change and recreate the wrapper functions in FlipbookPanel).
  const selectedLayerRef = useRef(null);
  useEffect(() => { selectedLayerRef.current = selectedLayer; }, [selectedLayer]);

  const onMediaClick = useCallback((layer, pageIdx = null) => {
    // Calling setSelectedLayerPageIndex *inside* the setSelectedLayer updater
    // can silently drop the secondary update due to React's batching rules.
    // Call both setters at the top level so React schedules them together.
    const isNew = layer?.id !== selectedLayerRef.current?.id;

    const next = isNew ? layer : null;
    setSelectedLayer(next);
    setSelectedLayerPageIndex(isNew ? pageIdx : null);
  }, []);

  // ─── Per-page button handlers ─────────────────────────────────────────────
  const handleEditStoryForPage = useCallback((e, pageIdx, isFromLeft, pageKind) => {
    e.preventDefault();
    e.stopPropagation();
    // push(`/story/${storyDetails?.id}/edit?pageIndex=${pageIdx}`, {
    //   from: `${window.location.href}&pageIndex=${pageIdx}`,
    // });

    if (pageKind) {
      push(`/child-journals/${currentJournalId}/${pageKind}`, {
        from: `${window.location.href}&ref=page-${pageIdx}`,
      });
      return;
    }

    const mediaId = selectedLayer?.['source-media-id'] || selectedLayer?.photo?.id;
    if (mediaId) {
      push(`/story/${(isFromLeft ? storyDetailsLeft : storyDetailsRight)?.id}/edit?ref=${mediaId}`, {
        from: `${window.location.href}&ref=${mediaId}`,
      });
    } else {
      push(`/story/${(isFromLeft ? storyDetailsLeft : storyDetailsRight)?.id}/edit?pageIndex=${pageIdx}`, {
        from: `${window.location.href}&pageIndex=${pageIdx}`,
      });
    }
  }, [storyDetailsLeft?.id, storyDetailsRight?.id, push, selectedLayerPageIndex, selectedLayer]);

  const handleChangeLayoutForPage = useCallback((pageIdx, page) => {
    if (page) {
      setCurrentPage(page);
      setSelectedPageIndex(pageIdx);
    }
    setChangeTemplate(true);
  }, [setCurrentPage, setSelectedPageIndex]);

  // The move/resize selection (layerEditor) is a different mechanism from the
  // legacy single-layer `selectedLayer` click-to-crop state above — media now
  // gets selected as a group, via the overlay in SliderPageRenderer. Crop
  // Photo opens the editor for whichever selected layer actually qualifies.
  const handleCropPhotoForPage = useCallback(() => {
    if (selectedLayer) {
      setSelectedImageLayer(selectedLayer);
      return;
    }
    const qualifyingLayer = layerEditor?.layers?.find(
      layer =>
        layerEditor.selectedLayerIds.includes(layer.id) &&
        layer.type === 'Books::PageImageLayer' &&
        layer.kind === 'user'
    );

    if (!qualifyingLayer) return;

    // The layer list above always reflects the server/baseline dimensions —
    // if the member just resized this image (unsaved), ImageEditor must open
    // with that live width/height/xpos/ypos, not the stale pre-resize ones,
    // so the crop frame actually matches what they see in the book preview.
    const liveRect = layerEditor.getRect(qualifyingLayer.id);
    setSelectedImageLayer({ ...qualifyingLayer, ...liveRect, type: 'book-page-layers' });
  }, [selectedLayer, layerEditor]);

  const handleCloseImageEditor = useCallback(() => {
    setSelectedImageLayerData({});
    setSelectedImageLayer(null);
    setSelectedLayer(null);
  }, []);

  const dispatch = useDispatch();
  // Explicit "Save changes" click — distinct from the unsaved-changes guard's
  // own save (triggered when the member tries to navigate away instead);
  // both show the same "Changes saved" toast via the app's existing Checkmark.
  const handleSaveChanges = useCallback(async () => {
    const ok = await layerEditor?.save?.();
    if (ok) {
      revalidateBookStoriesRef.current?.();
      revalidateBookStoriesRightRef.current?.();
      revalidatePage();
      setCheckMarkSuccess({ message: 'Changes saved', visibleDuration: 3000, top: '20px', height: '50px', width: '220px' });
    }
  }, [layerEditor, dispatch]);

  const { member } = useEntity({
    type: 'members',
    id: storyDetailsLeft?.['member-id'] || '',
  });

  // ─── Book dimensions ──────────────────────────────────────────────────────
  // innerBoxWidth is now the CSS layout (intrinsic) width from offsetWidth, so
  // it must be scaled by zoomValue to get the actual visual pixel width passed
  // to the FlipbookPanel container.  pageRenderHeight is the same principle.
  const pageRenderHeight = sliderHeight > 0 ? sliderHeight * (zoomValue / 100) : 0;
  const bookWidth = innerBoxWidth > 0 ? innerBoxWidth * (zoomValue / 100) * 2 : 0;

  // ─── Drag helpers bound to page width ────────────────────────────────────
  const pageWidth = innerBoxWidth || 300;

  return (
    <>
      {/* ── Image editor modal ─────────────────────────────────────────────── */}
      {Object.keys(selectedImageLayerData).length > 0 && (storyDetailsLeft || storyDetailsRight) ? (
        <Modal onClose={handleCloseImageEditor} maxWidth={600} isActive={false}>
          <Modal.InnerWrap>
            <Modal.Title
              style={{ color: '#000', fontFamily: 'Poppins', fontSize: '22px', fontWeight: '400', lineHeight: '165%' }}
            >
              Crop Photo
            </Modal.Title>
          </Modal.InnerWrap>
          <ImageEditor
            photoLayer={selectedImageLayer}
            imageSrc={selectedImageLayerData}
            firstBookId={firstBookId}
            pageId={currentPage?.id}
            revalidateBookStories={() => {
              revalidateBookStories()
              revalidateBookStoriesRight()
            }}
            setSelectedImageLayerData={setSelectedImageLayerData}
            setSelectedImageLayer={setSelectedImageLayer}
            setSelectedLayer={setSelectedLayer}
          />
        </Modal>
      ) : null}
      {!!showTextModal && <Modal
        bg="white"
        onClose={() => {
          setDataLayerAttributes(null)
          setShowTextModal(null)
        }}
        maxWidth={620}
        closeButtonLeft
        isActive={false}
      >
        <Modal.InnerWrap style={{ padding: 0, maxWidth: 620, margin: "auto", marginTop: '0' }}>
          <Modal.Title style={{
            color: '#000',
            fontFamily: 'Poppins',
            fontSize: '22px',
            fontWeight: '400',
            lineHeight: '165%',
            marginTop: '10px',
          }}> Edit Text</Modal.Title>
          <Box style={{ background: 'white', display: 'grid', borderRadius: '10px', paddingTop: '30px' }}>
            <Formik
              initialValues={dataLayerAttributes}
              validate={values => {
                const errors = {};

                if (String(values.text || "").replace(/<\/p\s*>/gi, '\n')
                  .replace(/<br\s*\/?>/gi, '\n')
                  .replace(/<[^>]*>/g, '')
                  .trim().length > maxChars) {
                  errors.text = `Text is too long — please shorten it to fit within the text box (max ~${maxChars} characters).`;
                }
                return errors;
              }}
              onSubmit={async values => {
                try {
                  const mediaId = showTextModal?.['source-media']?.id;
                  const attributes = pick(values, 'text', 'id', 'lock-version');

                  await updateTextMedium(mediaId, { data: { attributes } });

                  update({
                    entityType: 'texts',
                    id: mediaId,
                    attrs: { ...values, ...attributes },
                  });

                  update({
                    entityType: 'book-page-layers',
                    id: showTextModal?.id,
                    attrs: { ...showTextModal, text: values.text },
                  });
                  setDataLayerAttributes(null)
                  setShowTextModal(null);
                } catch (err) {
                  console.error(err);
                }
              }}
              render={formikProps => (
                <TextForm
                  {...formikProps}
                  onCancel={() => {
                    setDataLayerAttributes(null)
                    setShowTextModal(null)
                  }}
                  maxChars={maxChars}
                  showCount
                />
              )}
            />
          </Box>
        </Modal.InnerWrap>
      </Modal>}
      {/* ── Layout picker modal ────────────────────────────────────────────── */}
      {/* Show possibilities for whichever page the user clicked "Change Layout" on */}
      {!!changeTemplate && (() => {
        const modalPossibilities = currentPage?.id === rightPageId ? rightPagePossibilities : leftPagePossibilities;
        const modalIsValidating = currentPage?.id === rightPageId ? rightPageIsValidating : leftPageIsValidating;
        return updatingTemplate || modalPossibilities?.length > 0 || modalIsValidating;
      })() && (
          <Modal
            onClose={() => setChangeTemplate(null)}
            isActive={false}
            shadeProps={{ pt: [0, 1] }}
            maxWidth="95vw"
            closeButtonLeft
          >
            <Modal.InnerWrap>
              <Modal.Title
                style={{ color: '#000', fontFamily: 'Poppins', fontSize: '22px', fontWeight: '400', lineHeight: '165%' }}
              >
                Select a Layout
              </Modal.Title>
            </Modal.InnerWrap>
            {updatingTemplate ? (
              <Box height="90vh"><Loading /></Box>
            ) : (
              <Grid
                gridTemplateColumns={`repeat(auto-fill, ${width > 2000 ? '250px' : '300px'})`}
                justifyContent="center"
                gridGap="10px"
                width="100%"
                p={2}
              >
                {(currentPage?.id === rightPageId ? rightPagePossibilities : leftPagePossibilities).map((page, idx) => (
                  <Box
                    key={page?.id}
                    sx={{
                      transform: `scale(${zoomValueModal / 100})`,
                      transformOrigin: 'top left',
                      width: 'max-content',
                      height: width > 2000 ? '330px' : '380px',
                      borderRadius: '8.176px',
                      background: '#FAFAFA',
                      boxShadow: '0 1.626px 3.253px 0.813px rgba(0, 0, 0, 0.15)',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onPageSelection(page);
                    }}
                  >
                    <SliderPageRenderer
                      key={idx}
                      index={idx}
                      {...page}
                      zoom={100}
                      isSelected={false}
                      attributes={page.attributes || page}
                      type="page-possibility-layers"
                      isFromPossibilityReview={true}
                      setCurrentPage={setCurrentPage}
                    />
                  </Box>
                ))}
              </Grid>
            )}
          </Modal>
        )}

      {/* ── Main panel ─────────────────────────────────────────────────────── */}
      <FlipbookPanel
        divWidth={divWidth}
        isReadOnly={isReadOnly}
        isFromTellAStory={sessionStorage.getItem('fromMarcom')}
        // Story header
        storyDetailsLeft={storyDetailsLeft}
        storyDetailsRight={storyDetailsRight}
        member={member}
        zIndex={isFromTellAStory ? -1 : 4}
        // Navigation
        handlePrev={!showTextModal ? handlePrev : undefined}
        handleNext={!showTextModal ? handleNext : undefined}
        canFlipBackward={canFlipBackward}
        canFlipForward={canFlipForward}
        selectedPageIndex={selectedPageIndex}
        push={push}
        HeaderComponent={HeaderComponent}
        // Book dimensions
        bookWidth={bookWidth}
        pageRenderHeight={pageRenderHeight}

        // Animation / drag
        isAnimating={isAnimating}
        showFlippingElement={showFlippingElement}
        flipDirection={flipDirection}
        flipRotation={flipRotation}
        onDragStart={isReadOnly ? onDragStart : undefined}
        onDragMove={isReadOnly ? onDragMove : undefined}
        onDragEnd={isReadOnly ? onDragEnd : undefined}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        pageWidth={pageWidth}
        setContainerHeight={setContainerHeight}

        displayLeftPage={displayLeftPage}
        displayRightPage={displayRightPage}
        leftIdx={leftIdx}
        rightIdx={rightIdx}

        flipFrontPage={flipFrontPage}
        flipFrontIdx={flipFrontIdx}
        flipBackPage={flipBackPage}
        flipBackIdx={flipBackIdx}

        zoomValue={zoomValue}
        sliderHeight={sliderHeight}
        setCurrentPage={setCurrentPage}
        onMediaClick={onMediaClick}
        selectedLayer={selectedLayer}
        setSelectedLayer={setSelectedLayer}
        handleSliderHeightRef={handleSliderHeightRef}
        setMeasureRef={setMeasureRef}
        setShowTextModal={() => setShowTextModal(selectedLayer)}
        leftPagePossibilities={leftPagePossibilities}
        rightPagePossibilities={rightPagePossibilities}
        leftPageIsValidating={leftPageIsValidating}
        rightPageIsValidating={rightPageIsValidating}
        setChangeTemplate={setChangeTemplate}
        setSelectedImageLayer={setSelectedImageLayer}
        currentPage={currentPage}
        selectedLayerPageIndex={selectedLayerPageIndex}
        onEditStory={handleEditStoryForPage}
        onChangeLayout={handleChangeLayoutForPage}
        onCropPhoto={handleCropPhotoForPage}
        loading={loading}

        // Move/resize media editing
        mediaLayerEditor={overlayLayerEditor}
        editablePageId={editablePageId}
        // Unlike mediaLayerEditor (interactivity: selection/drag/resize,
        // only wired up for the current editable page), this covers EVERY
        // displayed page so an unsaved edit keeps rendering even once the
        // member navigates to (or just views) a different page.
        getLayerOverride={layerEditor?.getOverrideForPage}
        hasSelection={!!layerEditor && layerEditor.selectedLayerIds.length > 0}
        canCropSelection={canCropSelection}
        leftPageLock={leftPageLockState}
        rightPageLock={rightPageLockState}
        canUndo={!!layerEditor?.canUndo}
        canRedo={!!layerEditor?.canRedo}
        onUndo={layerEditor?.undo}
        onRedo={layerEditor?.redo}
        canSaveChanges={!!layerEditor?.hasUnsavedChanges}
        isSavingChanges={!!layerEditor?.isSaving}
        onSaveChanges={handleSaveChanges}
        checkMarkSuccess={checkMarkSuccess}
        setCheckMarkSuccess={setCheckMarkSuccess}
      />
    </>
  );
}
